import { Briefcase, Layers, TrendingUp } from "lucide-react";

import type { DealDashboardMetrics } from "@/api/client";
import { StatCard } from "@/components/StatCard";
import { formatAud } from "@/lib/currency";

export interface ManagerPerformancePanelProps {
  metrics: DealDashboardMetrics | undefined;
  /** CHP-804: the dashboard query failed — show an error state instead of an
   * endless "Loading metrics…" (which `!metrics` alone also produces). */
  isError?: boolean;
}

/** Three top-of-dashboard scorecards for the CM/RM/RA view (CHP-222 split
 * the former two-card row into three):
 *
 * - **Active Pipeline** — total $ across all open (LIVE) deals.
 * - **Pipeline Volume** — the count of those open deals (split out of the
 *   old "Active Pipeline Value" card's subtitle).
 * - **Closed Deals (Quarterly)** — total $ of deals won in the current
 *   calendar quarter plus the count. The quarter boundary is computed
 *   server-side (calendar quarter; switchable in routers/deals.py).
 *
 * Returns a loading row if metrics aren't ready yet so the layout
 * doesn't shift when the query resolves.
 */
export function ManagerPerformancePanel({ metrics, isError }: ManagerPerformancePanelProps) {
  if (isError && !metrics) {
    return (
      <p role="alert" className="mb-6 text-sm text-destructive" data-testid="manager-metrics-error">
        Couldn&apos;t load metrics. Please refresh to try again.
      </p>
    );
  }
  if (!metrics) {
    return (
      <p role="status" className="mb-6 text-sm text-muted-foreground">
        Loading metrics...
      </p>
    );
  }

  return (
    <div className="mb-6 grid gap-4 sm:grid-cols-3">
      <StatCard
        title="Active Pipeline"
        value={formatAud(metrics.active_deals_value)}
        icon={Briefcase}
        tone="neutral"
        testId="manager-active-pipeline"
        empty={metrics.active_deals_value === 0}
        emptyHint="No live deals yet"
      />
      <StatCard
        title="Pipeline Volume"
        value={metrics.active_deals}
        subtitle={`open deal${metrics.active_deals === 1 ? "" : "s"}`}
        icon={Layers}
        tone="neutral"
        testId="manager-pipeline-volume"
        empty={metrics.active_deals === 0}
        emptyHint="No live deals yet"
      />
      {/* Closed deals — genuine positive achievement. */}
      <StatCard
        title="Closed Deals (Quarterly)"
        value={formatAud(metrics.closed_won_value_quarter)}
        subtitle={`${metrics.closed_won_count_quarter} deal${metrics.closed_won_count_quarter === 1 ? "" : "s"} closed`}
        icon={TrendingUp}
        tone="success"
        testId="manager-closed-won-quarter"
        empty={metrics.closed_won_count_quarter === 0}
        emptyHint="No closed deals this quarter"
      />
    </div>
  );
}
